import { useState, useMemo } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { formatCurrency as formatCurrencyBase } from "../../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { SearchableSelect } from "../ui/searchable-select";
import { Checkbox } from "../ui/checkbox";
const MaterialsTab = ({ materials, materialUnits, materialCategories, billingCategories = [], getAuthHeaders, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleCategorySelection = (categoryMaterials) => {
    const ids = categoryMaterials.map((m) => m.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) => (allSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))));
  };
  const applyBulkCategory = async (clear = false) => {
    if (!selectedIds.length) return;
    setBulkBusy(true);
    try {
      const res = await apiClient.put(`/materials/bulk-billing-category`, { ids: selectedIds, billing_category: clear ? null : bulkCategory });
      toast.success(clear ? `Cleared billing category on ${res.data.matched} material(s)` : `Set "${bulkCategory}" on ${res.data.matched} material(s)`);
      setSelectedIds([]);
      setBulkCategory("");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };
  const [form, setForm] = useState({
    name: "", unit: "", unit_price: "", category: "", description: "", user_defined_price: false, billing_category: "", billing_category_per_job: false
  });
  
  // Track which categories are expanded (all expanded by default)
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const categories = [...new Set(materials.map(m => m.category || "Uncategorized"))];
    return categories.reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
  });

  // Group materials by category (with search filter applied)
  const materialsByCategory = useMemo(() => {
    const filtered = materials.filter((m) => {
      if (!search.trim()) return true;
      const searchable = [m.name, m.description, m.category, m.unit, m.billing_category].filter(Boolean).join(" ");
      return fuzzyMatch(search, searchable);
    });
    const grouped = {};
    filtered.forEach(m => {
      const cat = m.category || "Uncategorized";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(m);
    });
    // Sort categories alphabetically, but put "Uncategorized" last
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === "Uncategorized") return 1;
        if (b === "Uncategorized") return -1;
        return a.localeCompare(b);
      })
      .reduce((acc, key) => {
        acc[key] = grouped[key].sort((a, b) => a.name.localeCompare(b.name));
        return acc;
      }, {});
  }, [materials, search]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAll = () => {
    const allExpanded = Object.keys(materialsByCategory).reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
    setExpandedCategories(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed = Object.keys(materialsByCategory).reduce((acc, cat) => ({ ...acc, [cat]: false }), {});
    setExpandedCategories(allCollapsed);
  };

  const resetForm = () => {
    setForm({ name: "", unit: "", unit_price: "", category: "", description: "", user_defined_price: false, billing_category: "", billing_category_per_job: false });
    setEditingMaterial(null);
  };

  const openEditMaterial = (m) => {
    setEditingMaterial(m);
    setForm({
      name: m.name, unit: m.unit, unit_price: m.unit_price.toString(),
      category: m.category, description: m.description || "",
      user_defined_price: m.user_defined_price || false,
      billing_category: m.billing_category || "",
      billing_category_per_job: m.billing_category_per_job || false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...form, 
        unit_price: parseFloat(form.unit_price) || 0,
        user_defined_price: form.user_defined_price,
        billing_category: form.billing_category_per_job ? null : (form.billing_category || null),
        billing_category_per_job: !!form.billing_category_per_job
      };
      if (editingMaterial) {
        await apiClient.put(`/materials/${editingMaterial.id}`, payload);
        toast.success("Material updated");
      } else {
        await apiClient.post(`/materials`, payload);
        toast.success("Material created");
      }
      onRefresh();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/materials/${id}`);
      toast.success("Material deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  const formatCurrency = (amount) => formatCurrencyBase(amount, true);

  const categoryCount = Object.keys(materialsByCategory).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Materials</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">{materials.length} materials in {categoryCount} categories</p>
        </div>
        <div className="flex items-center gap-2">
          {categoryCount > 1 && (
            <div className="flex items-center gap-1 mr-2">
              <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs h-8">
                Expand All
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs h-8">
                Collapse All
              </Button>
            </div>
          )}
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-material-btn"><Plus className="w-4 h-4 mr-2" />Add Material</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingMaterial ? "Edit Material" : "Add New Material"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <Label>Material Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="e.g., Red Clay Brick" data-testid="material-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Unit</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                      <SelectTrigger data-testid="material-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...materialUnits].sort((a, b) => a.localeCompare(b)).map((u) => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="form-group">
                    <Label>Unit Price ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.unit_price} 
                      onChange={(e) => setForm({...form, unit_price: e.target.value})} 
                      disabled={form.user_defined_price}
                      placeholder={form.user_defined_price ? "Set per job" : "0.75"} 
                      data-testid="material-price-input" 
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
                  <Checkbox 
                    id="user-defined-price" 
                    checked={form.user_defined_price} 
                    onCheckedChange={(checked) => setForm({...form, user_defined_price: checked, unit_price: checked ? "0" : form.unit_price})}
                    data-testid="user-defined-price-checkbox"
                  />
                  <div>
                    <Label htmlFor="user-defined-price" className="cursor-pointer font-medium">User Defined Price</Label>
                    <p className="text-xs text-muted-foreground">Enable this for materials with variable pricing. Price will be entered when adding to a job.</p>
                  </div>
                </div>
                <div className="form-group">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="material-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...materialCategories].sort((a, b) => a.localeCompare(b)).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
                  <Checkbox
                    id="billing-category-per-job"
                    checked={!!form.billing_category_per_job}
                    onCheckedChange={(checked) => setForm({ ...form, billing_category_per_job: !!checked, billing_category: checked ? "" : form.billing_category })}
                    data-testid="billing-category-per-job-checkbox"
                  />
                  <Label htmlFor="billing-category-per-job" className="text-sm cursor-pointer">
                    Set billing category in bid sheet
                    <span className="block text-xs text-muted-foreground font-normal">Required per line when added to a bid (e.g. veneer anchors → Brick, Stone or Cast Stone depending on the job)</span>
                  </Label>
                </div>
                {!form.billing_category_per_job && (
                <div className="form-group">
                  <Label>Billing Category</Label>
                  <SearchableSelect
                    value={form.billing_category}
                    onValueChange={(v) => setForm({ ...form, billing_category: v || "" })}
                    options={billingCategories.map((c) => ({ value: c, label: c }))}
                    placeholder={billingCategories.length ? "Select billing category (optional)" : "Add billing categories in Options"}
                    searchPlaceholder="Search billing categories..."
                    data-testid="material-billing-category-select"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Groups this material on the Schedule of Values when the first billing is created.</p>
                </div>
                )}
                <div className="form-group">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} data-testid="material-description-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-material-btn">
                  {editingMaterial ? "Update Material" : "Create Material"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search materials..." className="pl-9 pr-9" data-testid="materials-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        {selectedIds.length > 0 && (
          <div className="mx-4 mb-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="bulk-billing-bar">
            <span className="text-sm font-medium text-emerald-900" data-testid="bulk-selected-count">{selectedIds.length} selected</span>
            <div className="flex-1 sm:max-w-xs">
              <SearchableSelect
                value={bulkCategory}
                onValueChange={(v) => setBulkCategory(v || "")}
                options={billingCategories.map((c) => ({ value: c, label: c }))}
                placeholder="Billing category to apply"
                searchPlaceholder="Search billing categories..."
                data-testid="bulk-billing-category-select"
              />
            </div>
            <Button size="sm" onClick={() => applyBulkCategory(false)} disabled={!bulkCategory || bulkBusy} data-testid="bulk-billing-apply-btn">Apply to selected</Button>
            <Button size="sm" variant="outline" onClick={() => applyBulkCategory(true)} disabled={bulkBusy} data-testid="bulk-billing-clear-btn">Clear category</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="bulk-billing-deselect-btn">Deselect all</Button>
          </div>
        )}
        {Object.keys(materialsByCategory).length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <p>No materials added yet</p>
            <p className="text-sm mt-1">Click &quot;Add Material&quot; to get started</p>
          </div>
        ) : (
          <div className="divide-y">
            {Object.entries(materialsByCategory).map(([category, categoryMaterials]) => (
              <Collapsible 
                key={category} 
                open={expandedCategories[category]} 
                onOpenChange={() => toggleCategory(category)}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors" data-testid={`category-toggle-${category}`}>
                  <div className="flex items-center gap-2">
                    {expandedCategories[category] ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="font-semibold">{category}</span>
                    <span className="text-sm text-muted-foreground">({categoryMaterials.length})</span>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header bg-muted/30">
                        <TableHead className="w-10 pl-4">
                          <Checkbox
                            checked={categoryMaterials.every((m) => selectedIds.includes(m.id))}
                            onCheckedChange={() => toggleCategorySelection(categoryMaterials)}
                            aria-label={`Select all in ${category}`}
                            data-testid={`select-category-${category}`}
                          />
                        </TableHead>
                        <TableHead className="w-[38%]">Material</TableHead>
                        <TableHead className="w-[15%]">Unit</TableHead>
                        <TableHead className="text-right w-[25%]">Unit Price</TableHead>
                        <TableHead className="text-right w-[20%]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryMaterials.map((m) => (
                        <TableRow key={m.id} className={`table-row ${selectedIds.includes(m.id) ? "bg-emerald-50/60" : ""}`}>
                          <TableCell className="w-10 pl-4">
                            <Checkbox
                              checked={selectedIds.includes(m.id)}
                              onCheckedChange={() => toggleSelected(m.id)}
                              aria-label={`Select ${m.name}`}
                              data-testid={`select-material-${m.id}`}
                            />
                          </TableCell>
                          <TableCell className="w-[38%]">
                            <div>
                              <p className="font-medium">{m.name}</p>
                              {m.description && <p className="text-sm text-muted-foreground truncate max-w-xs">{m.description}</p>}
                              {m.billing_category && <span className="inline-flex mt-0.5 px-1.5 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`material-billing-badge-${m.id}`}>Billing: {m.billing_category}</span>}
                            </div>
                          </TableCell>
                          <TableCell className="w-[15%]">{m.unit}</TableCell>
                          <TableCell className="text-right font-mono w-[25%]">
                            {m.user_defined_price ? (
                              <span className="text-amber-600 font-medium">Variable</span>
                            ) : (
                              <>{formatCurrency(m.unit_price)}/{m.unit}</>
                            )}
                          </TableCell>
                          <TableCell className="text-right w-[20%]">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditMaterial(m)} data-testid={`edit-material-${m.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-material-${m.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Material?</AlertDialogTitle>
                                    <AlertDialogDescription>This will permanently remove {m.name}.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(m.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MaterialsTab;
